import type { Notification, NotificationListResponse, NotificationStats } from "@/domain";
import { InvalidationEntity } from "../entityDependencies";
import { queryKeys } from "../queryKeys";
import { isObject, type CacheEntry, type CacheSlot } from "../cache/cacheSlots";
import { aggregateSlot, type AggregateDelta } from "../cache/aggregateSlots";
import { defineEntityCache } from "../cache/entityCache";
import {
  byUuid,
  distinctPages,
  viewSlot,
  type ViewRules,
  type ViewShape,
} from "../cache/listViews";

/** Stands for every notification of the inbox (mark all, delete all). */
export const EVERY_NOTIFICATION = "*";

/** Notifications belong to the user, not to an organization. */
export const USER_INBOX = "";

export interface NotificationPatch {
  is_read: boolean;
}

interface Paged {
  pages: NotificationListResponse[];
  pageParams: unknown[];
}

const page: ViewShape<NotificationListResponse, Notification, string> = {
  isPage: (value): value is NotificationListResponse =>
    isObject(value) && Array.isArray(value.notifications) && typeof value.total_count === "number",
  records: (current) => current.notifications,
  withRecords: (current, notifications, countDelta) => ({
    ...current,
    notifications,
    total_count: Math.max(0, current.total_count + countDelta),
  }),
  idOf: (notification) => notification.uuid,
  total: (current) => current.total_count,
};

export const distinctNotificationPages = distinctPages(page);

const isPaged = (value: unknown): value is Paged =>
  isObject(value) &&
  Array.isArray(value.pageParams) &&
  Array.isArray(value.pages) &&
  value.pages.every(page.isPage);

const isStats = (value: unknown): value is NotificationStats =>
  isObject(value) && typeof value.unread_count === "number" && typeof value.read_count === "number";

const merge = (notification: Notification, { is_read }: NotificationPatch): Notification =>
  notification.is_read === is_read
    ? notification
    : { ...notification, is_read, read_at: is_read ? new Date().toISOString() : null };

export const notificationRules: ViewRules<Notification> = {
  accepts: {
    is_read: (value, notification) => typeof value !== "boolean" || value === notification.is_read,
  },
  narrowing: ["notification_type", "search", "created_from", "created_to"],
  order: { created_at: (notification) => Date.parse(notification.created_at) },
  tieBreak: byUuid("desc", (notification) => notification.uuid),
};

function everyPage(
  entries: readonly CacheEntry[],
  change: (current: NotificationListResponse, key: CacheEntry[0]) => NotificationListResponse,
): CacheEntry[] {
  const changed: CacheEntry[] = [];
  for (const [key, data] of entries) {
    if (!isPaged(data)) continue;
    const pages = data.pages.map((current) => change(current, key));
    if (pages.some((current, index) => current !== data.pages[index]))
      changed.push([key, { ...data, pages }]);
  }
  return changed;
}

const emptied = (current: NotificationListResponse) =>
  current.notifications.length === 0 && current.total_count === 0
    ? current
    : { ...current, notifications: [], total_count: 0 };

const inbox = viewSlot({
  prefix: queryKeys.notifications.lists(),
  shape: page,
  merge,
  parse: queryKeys.notifications.parseList,
  rules: notificationRules,
});

/** One notification moves between the read and unread tabs; «all» empties the tab it leaves. */
const inboxSlot: CacheSlot<string, NotificationPatch, Notification> = {
  ...inbox,
  patchAll: (entries, id, patch, change) =>
    id !== EVERY_NOTIFICATION
      ? inbox.patchAll(entries, id, patch, change)
      : everyPage(entries, (current, key) =>
          queryKeys.notifications.parseList(key)?.filters.is_read === !patch.is_read
            ? emptied(current)
            : page.withRecords(
                current,
                current.notifications.map((notification) => merge(notification, patch)),
                0,
              ),
        ),
  removeAll: (entries, id, change) =>
    id !== EVERY_NOTIFICATION ? inbox.removeAll(entries, id, change) : everyPage(entries, emptied),
};

function statsDelta(data: NotificationStats, delta: AggregateDelta<Notification>) {
  const weight = (notification: Notification | undefined) => ({
    total: notification ? 1 : 0,
    unread: notification && !notification.is_read ? 1 : 0,
  });
  const added = weight(delta.added);
  const removed = weight(delta.removed);
  const total = data.total_count + added.total - removed.total;
  const unread = data.unread_count + added.unread - removed.unread;
  if (total === data.total_count && unread === data.unread_count) return data;
  return { ...data, total_count: total, unread_count: unread, read_count: total - unread };
}

const stats = aggregateSlot({
  prefix: queryKeys.notifications.stats(),
  rules: notificationRules,
  parse: queryKeys.notifications.parseStats,
  isData: isStats,
  apply: statsDelta,
});

/** The unread badge follows every change; «all» sets it to what the server will answer. */
const statsSlot: CacheSlot<string, NotificationPatch, Notification> = {
  ...stats,
  patchAll: (entries, id, patch, change) =>
    id !== EVERY_NOTIFICATION
      ? stats.patchAll(entries, id, patch, change)
      : entries.flatMap(([key, data]): CacheEntry[] =>
          isStats(data) && patch.is_read
            ? [[key, { ...data, unread_count: 0, read_count: data.total_count }]]
            : [],
        ),
  removeAll: (entries, id, change) =>
    id !== EVERY_NOTIFICATION
      ? stats.removeAll(entries, id, change)
      : entries.flatMap(([key, data]): CacheEntry[] =>
          isStats(data) ? [[key, { total_count: 0, unread_count: 0, read_count: 0 }]] : [],
        ),
};

export const notificationCache = defineEntityCache<string, NotificationPatch, Notification>({
  entity: InvalidationEntity.NOTIFICATION,
  row: merge,
  slots: () => [inboxSlot, statsSlot],
  records: () => [],
});
